import React, { useContext } from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import { Formik, Field, FieldProps, Form } from 'formik';
import { useQuery, useMutation } from '@apollo/client/react/hooks';
import PropTypes from 'prop-types';

import Modal from 'four-nets/ui-v2/modal/modal';
import { VendorInformationFragment } from 'four-nets/photoblog/user-blog/author-side/author-side-components/vendor-information/vendor-information.graphql';
import Input from 'four-nets/ui-v2/form/input/input';
import TextArea from 'four-nets/ui-v2/form/text-area/text-area';
import {
    mergeValidators,
    emptyValidator,
    emailValidator,
    minMaxLengthValidator,
    regexValidator,
} from 'four-nets/ui-v2/form/validators';
import CountySelect from 'four-nets/photoblog/users/county-select/county-select';
import { Site } from 'four-nets/site';
import Select from 'four-nets/ui-v2/select/select';
import countySelectQuery, {
    CountySelectQuery,
    CountySelectQueryVariables,
} from 'four-nets/photoblog/users/county-select/county-select-query.graphql';
import catalogCategoriesQuery, {
    CatalogCategoriesQuery,
    CatalogCategoriesQueryVariables,
} from 'four-nets/vendor/homepage/register-form/catalog-categories.graphql';
import { AlertContext } from 'lib/alert-context/alert-context';
import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';

import mutation, {
    UpdateVendorInformationMutation,
    UpdateVendorInformationMutationVariables,
} from './update-vendor-info.graphql';
import defaultStyles from './update-vendor-info.scss';
import { toGlobalId } from 'graphql-relay';

interface Values {
    name: string;
    category: string;
    web: string;
    address: string;
    phone: string;
    cardText: string;
    facebookPageUrl: string;
    email: string;
    county: string;
    instagramPageUrl: string;
}

type Errors = Partial<{ [key in keyof Values]: Array<React.ReactElement<FormattedMessage>> | true }>;

interface Props {
    styles: typeof defaultStyles;
    open?: boolean;
    setOpen: (value: boolean) => void;
    vendor: VendorInformationFragment;
}

const UpdateVendorInfo: React.FC<Props> = (props, context) => {
    const { styles, open, setOpen, vendor } = props;

    let countryName;
    if (context && context.site.isSlovak) {
        countryName = 'SLOVAKIA';
    } else if (context && context.site.isCzech) {
        countryName = 'CZECH_REPUBLIC';
    }

    const { data } = useQuery<CountySelectQuery, CountySelectQueryVariables>(countySelectQuery, {
        variables: { countryName },
    });
    const { data: catalogCategoriesData } = useQuery<CatalogCategoriesQuery, CatalogCategoriesQueryVariables>(
        catalogCategoriesQuery,
        { variables: { serverId: toGlobalId('ServerNode', context && context.site && context.site.id) } }
    );
    const [updateVendorInformation] = useMutation<
        UpdateVendorInformationMutation,
        UpdateVendorInformationMutationVariables
    >(mutation);
    const { fail } = useContext(AlertContext);

    if (!vendor) {
        return null;
    }

    return (
        <>
            <Modal
                title={<FormattedMessage id='EDIT_VENDOR_INFORMATION' />}
                open={Boolean(open)}
                onClose={() => setOpen(false)}
                dirty={true}
            >
                <Formik<Values>
                    initialValues={{
                        name: vendor.name || '',
                        category: (vendor.category && vendor.category.id) || '',
                        county: (vendor.county && vendor.county.id) || '',
                        address: vendor.address || '',
                        phone: vendor.phone || '',
                        email: (vendor.customer && vendor.customer.email) || '',
                        web: vendor.web || '',
                        cardText: vendor.cardText || '',
                        facebookPageUrl: vendor.facebookPageUrl || '',
                        instagramPageUrl: '',
                    }}
                    validate={values => {
                        const errors: Errors = {};
                        const nameErrors = mergeValidators([emptyValidator, minMaxLengthValidator(0, 100)])(
                            values.name
                        );
                        const categoryErrors = mergeValidators([emptyValidator])(values.category);
                        const phoneErrors = mergeValidators([minMaxLengthValidator(0, 100)])(values.phone);
                        const cardTextErrors = mergeValidators([emptyValidator, minMaxLengthValidator(0, 150)])(
                            values.cardText
                        );
                        const emailErrors = mergeValidators([emptyValidator, emailValidator])(values.email);
                        const webErrors = mergeValidators([minMaxLengthValidator(0, 100)])(values.web);

                        if (nameErrors) {
                            errors.name = nameErrors;
                        }
                        if (categoryErrors) {
                            errors.category = categoryErrors;
                        }
                        if (phoneErrors) {
                            errors.phone = phoneErrors;
                        }
                        if (cardTextErrors) {
                            errors.cardText = cardTextErrors;
                        }
                        if (emailErrors) {
                            errors.email = emailErrors;
                        }
                        if (webErrors) {
                            errors.web = webErrors;
                        }
                        return errors;
                    }}
                    onSubmit={(values, actions) => {
                        actions.setSubmitting(true);
                        updateVendorInformation({ variables: { input: { ...values } } })
                            .then(() => {
                                setOpen(false);
                            })
                            .catch(error => {
                                fail!({
                                    onAccept: () => null,
                                    body: JSON.stringify(
                                        error &&
                                            error.graphQLErrors &&
                                            error.graphQLErrors[0] &&
                                            error.graphQLErrors[0].message
                                    ),
                                    title: <FormattedMessage id='ERROR' />,
                                });
                            })
                            .finally(() => {
                                actions.setSubmitting(false);
                            });
                    }}
                >
                    {formik => {
                        const { isSubmitting, submitForm, setFieldValue, touched, errors } = formik;

                        return (
                            <LoadingWrapperView style={{ content: styles.padding }} loading={isSubmitting}>
                                <Form>
                                    <Field name='name'>
                                        {({ field }: FieldProps) => (
                                            <label className={styles.field}>
                                                <FormattedMessage id='VENDOR_NAME'>
                                                    {msg => <span className={styles.label}>{msg}*</span>}
                                                </FormattedMessage>
                                                <Input
                                                    {...field}
                                                    errors={errors.name ? (errors.name as any) : undefined}
                                                    touched={touched.name}
                                                />
                                            </label>
                                        )}
                                    </Field>
                                    <Field name='category'>
                                        {({ field }: FieldProps) => (
                                            <label className={styles.field}>
                                                <FormattedMessage id='CATEGORY'>
                                                    {msg => <div className={styles.label}>{msg}*</div>}
                                                </FormattedMessage>
                                                <Select
                                                    style={{ select: styles.select }}
                                                    {...field}
                                                    errors={errors.category ? (errors.category as any) : undefined}
                                                >
                                                    {catalogCategoriesData &&
                                                        catalogCategoriesData.catalogCategories &&
                                                        catalogCategoriesData.catalogCategories.edges.map(e => (
                                                            <option value={e!.node!.id} key={e!.node!.id}>
                                                                {e!.node!.name}
                                                            </option>
                                                        ))}
                                                </Select>
                                            </label>
                                        )}
                                    </Field>
                                    <Field name='county'>
                                        {({ field }: FieldProps) => (
                                            <label className={styles.field}>
                                                <FormattedMessage id='COUNTY'>
                                                    {msg => <div className={styles.label}>{msg}</div>}
                                                </FormattedMessage>
                                                {data && data.counties ? (
                                                    <CountySelect
                                                        style={{ select: styles.select } as any}
                                                        {...data.counties}
                                                        {...field}
                                                        errors={errors.county ? (errors.county as any) : undefined}
                                                    />
                                                ) : null}
                                            </label>
                                        )}
                                    </Field>
                                    <Field name='cardText'>
                                        {({ field }: FieldProps) => (
                                            <label className={styles.field}>
                                                <FormattedMessage id='CARD_TEXT'>
                                                    {msg => <span className={styles.label}>{msg}*</span>}
                                                </FormattedMessage>
                                                <TextArea
                                                    {...field}
                                                    errors={errors.cardText ? (errors.cardText as any) : undefined}
                                                    touched={touched.cardText}
                                                    onChange={e => setFieldValue('cardText', e.target.value)}
                                                />
                                            </label>
                                        )}
                                    </Field>
                                    <Field name='web'>
                                        {({ field }: FieldProps) => (
                                            <label className={styles.field}>
                                                <FormattedMessage id='WEB'>
                                                    {msg => <span className={styles.label}>{msg}</span>}
                                                </FormattedMessage>
                                                <Input
                                                    {...field}
                                                    errors={errors.web ? (errors.web as any) : undefined}
                                                    touched={touched.web}
                                                />
                                            </label>
                                        )}
                                    </Field>
                                    <Field name='email'>
                                        {({ field }: FieldProps) => (
                                            <label className={styles.field}>
                                                <FormattedMessage id='CUSTOMER_EMAIL'>
                                                    {msg => <span className={styles.label}>{msg}*</span>}
                                                </FormattedMessage>
                                                <Input
                                                    {...field}
                                                    errors={errors.email ? (errors.email as any) : undefined}
                                                    touched={touched.email}
                                                />
                                            </label>
                                        )}
                                    </Field>
                                    <Field name='phone'>
                                        {({ field }: FieldProps) => (
                                            <label className={styles.field}>
                                                <FormattedMessage id='PHONE'>
                                                    {msg => <span className={styles.label}>{msg}</span>}
                                                </FormattedMessage>
                                                <Input
                                                    {...field}
                                                    errors={errors.phone ? (errors.phone as any) : undefined}
                                                    touched={touched.phone}
                                                />
                                            </label>
                                        )}
                                    </Field>
                                    <Field name='address'>
                                        {({ field }: FieldProps) => (
                                            <label className={styles.field}>
                                                <FormattedMessage id='ADDRESS'>
                                                    {msg => <span className={styles.label}>{msg}</span>}
                                                </FormattedMessage>
                                                <Input
                                                    {...field}
                                                    errors={errors.address ? (errors.address as any) : undefined}
                                                    touched={touched.address}
                                                />
                                            </label>
                                        )}
                                    </Field>
                                    <Field name='facebookPageUrl'>
                                        {({ field }: FieldProps) => (
                                            <label className={styles.field}>
                                                <FormattedMessage id='FACEBOOK'>
                                                    {msg => <span className={styles.label}>{msg}</span>}
                                                </FormattedMessage>
                                                <Input
                                                    {...field}
                                                    errors={
                                                        errors.facebookPageUrl
                                                            ? (errors.facebookPageUrl as any)
                                                            : undefined
                                                    }
                                                    touched={touched.facebookPageUrl}
                                                />
                                            </label>
                                        )}
                                    </Field>
                                    <Field name='instagramPageUrl'>
                                        {({ field }: FieldProps) => (
                                            <label className={styles.field}>
                                                <FormattedMessage id='INSTAGRAM'>
                                                    {msg => <span className={styles.label}>{msg}</span>}
                                                </FormattedMessage>
                                                <Input
                                                    {...field}
                                                    errors={
                                                        errors.instagramPageUrl
                                                            ? (errors.instagramPageUrl as any)
                                                            : undefined
                                                    }
                                                    touched={touched.instagramPageUrl}
                                                />
                                            </label>
                                        )}
                                    </Field>
                                    <button
                                        type='button'
                                        className={styles.submitButton}
                                        onClick={submitForm}
                                        disabled={isSubmitting}
                                    >
                                        <FormattedMessage id='SAVE' values={{ gender: 'v' }} />
                                    </button>
                                </Form>
                            </LoadingWrapperView>
                        );
                    }}
                </Formik>
            </Modal>
        </>
    );
};

UpdateVendorInfo.contextTypes = {
    site: PropTypes.instanceOf(Site),
};

export default Styled(defaultStyles)(UpdateVendorInfo);
